const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

function getToken() {
  return localStorage.getItem("realtyflows_token");
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || errBody.message || "API error");
    if (errBody.code) err.code = errBody.code;
    throw err;
  }
  return res.json();
}

// Auth
export const signup = (data) => request("/auth/signup", { method: "POST", body: JSON.stringify(data) });
export const login = (data) => request("/auth/login", { method: "POST", body: JSON.stringify(data) });
export const googleAuth = (idToken, mode = "login") => request("/auth/google", { method: "POST", body: JSON.stringify({ idToken, mode }) });
export const getMe = () => request("/auth/me");
export const updateProfile = (data) => request("/auth/me", { method: "PUT", body: JSON.stringify(data) });
export const acceptAiDisclaimer = () => request("/auth/accept-ai-disclaimer", { method: "POST" });
export const getOnboardingStatus = () => request("/auth/onboarding");
export const dismissOnboarding = () => request("/auth/onboarding/dismiss", { method: "POST" });
export const uploadPhoto = async (file) => {
  const token = getToken();
  const formData = new FormData();
  formData.append("photo", file);
  const res = await fetch(`${API_BASE}/auth/upload-photo`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: formData,
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || "Upload failed"); }
  return res.json();
};
export const sendVerificationCode = (data) => request("/auth/send-code", { method: "POST", body: JSON.stringify(data) });
export const verifyVerificationCode = (data) => request("/auth/verify-code", { method: "POST", body: JSON.stringify(data) });
export const verifyEmailToken = (token, email) => request(`/auth/verify-token?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
export const checkVerification = (email) => request(`/auth/check-verification?email=${encodeURIComponent(email)}`);
export const changeEmail = (data) => request("/auth/change-email", { method: "POST", body: JSON.stringify(data) });
export const forgotPassword = (email) => request("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
export const resetPassword = (token, password) => request("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
export const resetPasswordByEmail = (email, password) => request("/auth/reset-password-by-email", { method: "POST", body: JSON.stringify({ email, password }) });

// Contact
export const submitContact = (data) => request("/contact", { method: "POST", body: JSON.stringify(data) });

// Site Settings (public, no auth)
export const getSiteTheme = () => fetch(`${API_BASE}/site-settings/theme`).then(r => r.json()).then(d => d.value || "classic").catch(() => "classic");
export const getFeatureFlag = (key) => fetch(`${API_BASE}/site-settings/${key}`).then(r => r.json()).then(d => d.value === true || d.value === "true").catch(() => false);

// Public Stats (no auth)
export const getPublicStats = () => request("/stats/public");

export const saveToken = (token) => localStorage.setItem("realtyflows_token", token);
export const removeToken = () => localStorage.removeItem("realtyflows_token");
export const hasToken = () => !!localStorage.getItem("realtyflows_token");

// Clients
export const getClients = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/clients${query ? `?${query}` : ""}`);
};
export const getClient = (id) => request(`/clients/${id}`);
export const createClient = (data) => request("/clients", { method: "POST", body: JSON.stringify(data) });
// CSV bulk import — body { rows: [...] }, max 1000 rows. Returns { created, updated, skipped }.
export const importClients = (rows) => request("/clients/import", { method: "POST", body: JSON.stringify({ rows }) });
export const updateClient = (id, data) => request(`/clients/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteClient = (id) => request(`/clients/${id}`, { method: "DELETE" });
export const getClientStats = () => request("/clients/stats/summary");
export const getWeeklyPerformance = () => request("/clients/stats/weekly");
export const getHotClients = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/clients/hot${query ? `?${query}` : ""}`);
};
// V2 Feature 2.6 — Next Best Actions
export const getNextActions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/clients/next-actions${query ? `?${query}` : ""}`);
};
export const scoreClient = (id) => request(`/clients/${id}/score`, { method: "POST" });
export const getClientScoreHistory = (id) => request(`/clients/${id}/score-history`);
export const updateEngagementPattern = (id, data) => request(`/clients/${id}/engagement-pattern`, { method: "PUT", body: JSON.stringify(data) });
export const logClientActivity = (id, data) => request(`/clients/${id}/activities`, { method: "POST", body: JSON.stringify(data) });

// Deals
export const getDeals = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/deals${query ? `?${query}` : ""}`);
};
export const getDeal = (id) => request(`/deals/${id}`);
export const getClientDeals = (clientId) => request(`/deals/client/${clientId}`);
export const createDeal = (data) => request("/deals", { method: "POST", body: JSON.stringify(data) });
export const updateDeal = (id, data) => request(`/deals/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteDeal = (id) => request(`/deals/${id}`, { method: "DELETE" });
export const getDealStats = () => request("/deals/stats/summary");
export const getDealsByStage = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/deals/stats/by-stage${query ? `?${query}` : ""}`);
};

// V2 Feature 2.11 — Offer management
export const getDealOffers = (dealId) => request(`/deals/${dealId}/offers`);
export const createDealOffer = (dealId, body) =>
  request(`/deals/${dealId}/offers`, { method: "POST", body: JSON.stringify(body) });
export const updateDealOffer = (dealId, offerId, body) =>
  request(`/deals/${dealId}/offers/${offerId}`, { method: "PUT", body: JSON.stringify(body) });
export const deleteDealOffer = (dealId, offerId) =>
  request(`/deals/${dealId}/offers/${offerId}`, { method: "DELETE" });

// Deal Documents & E-Sign
export const getDealDocuments = (dealId) => request(`/deals/${dealId}/documents`);
export const getDealMilestones = (dealId) => request(`/deals/${dealId}/milestones`);
export const getDocSignatures = (dealId, docId) => request(`/deals/${dealId}/documents/${docId}/signatures`);
export const getSignedPdfUrl = (dealId, docId) => {
  const token = getToken();
  return `${API_BASE}/deals/${dealId}/documents/${docId}/pdf${token ? `?token=${token}` : ""}`;
};
export const resendDocSign = (dealId, docId) => request(`/deals/${dealId}/documents/${docId}/resend`, { method: "POST" });
export const voidDocSign = (dealId, docId) => request(`/deals/${dealId}/documents/${docId}/void`, { method: "POST" });
export const completeMilestone = (dealId, milestoneId) => request(`/deals/${dealId}/milestones/${milestoneId}/complete`, { method: "POST" });

// Document Templates
export const getDocTemplates = (pipelineType) => request(`/templates${pipelineType ? `?pipeline_type=${pipelineType}` : ""}`);
export const getDocTemplate = (templateId) => request(`/templates/${templateId}`);
export const createDocTemplate = (data) => request(`/templates`, { method: "POST", body: JSON.stringify(data) });
export const updateDocTemplate = (templateId, data) => request(`/templates/${templateId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteDocTemplate = (templateId) => request(`/templates/${templateId}`, { method: "DELETE" });
export const reorderDocTemplates = (order) => request(`/templates/reorder`, { method: "PUT", body: JSON.stringify({ order }) });
export const getTemplateFields = (templateId, dealId) => request(`/templates/${templateId}/fields${dealId ? `?deal_id=${dealId}` : ""}`);
export const sendTemplate = (templateId, data) => request(`/templates/${templateId}/send`, { method: "POST", body: JSON.stringify(data) });
export const previewTemplate = async (templateId) => {
  const token = getToken();
  const res = await fetch(`${API_BASE}/templates/${templateId}/preview`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error("Preview failed");
  return res.text();
};

// Messages
export const getConversations = () => request("/messages/conversations");
export const getMessages = (clientId) => request(`/messages/client/${clientId}`);
export const sendMessage = (data) => request("/messages", { method: "POST", body: JSON.stringify(data) });
export const markRead = (clientId) => request(`/messages/read/${clientId}`, { method: "PUT" });

// Appointments
export const getAppointments = () => request("/appointments");
export const createAppointment = (data) => request("/appointments", { method: "POST", body: JSON.stringify(data) });
export const updateAppointment = (id, data) => request(`/appointments/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteAppointment = (id) => request(`/appointments/${id}`, { method: "DELETE" });

// Automations
export const getAutomations = () => request("/automations");
export const createAutomation = (data) => request("/automations", { method: "POST", body: JSON.stringify(data) });
export const toggleAutomation = (id) => request(`/automations/${id}/toggle`, { method: "PUT" });
export const updateAutomation = (id, data) => request(`/automations/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteAutomation = (id) => request(`/automations/${id}`, { method: "DELETE" });
export const getAutomationStats = () => request("/automations/stats/summary");

// Activity
export const getActivity = (limit = 10) => request(`/activity?limit=${limit}`);
export const logActivity = (data) => request("/activity", { method: "POST", body: JSON.stringify(data) });

// Teams
export const getFeatures = (plan) => request(`/teams/features?plan=${plan}`);
export const getMyTeam = (userId) => request(`/teams/my-team?user_id=${userId}`);

// V2 ADR-003 — multi-portal membership endpoints.
export const getMemberships = () => request("/auth/memberships");
export const activatePersonalPortal = () => request("/auth/activate-personal-portal", { method: "POST", body: JSON.stringify({}) });
// switchPortal({ team_id: number | null }) — returns { token, active_team_id }.
// Caller must persist the new token (overwrite the old one in localStorage).
export const switchPortal = (teamId) => request("/auth/switch-portal", {
  method: "POST",
  body: JSON.stringify({ team_id: teamId }),
});
export const createTeam = (data) => request("/teams/create", { method: "POST", body: JSON.stringify(data) });
export const createLiteTeam = () => request("/teams/create-lite", { method: "POST" });
export const addLiteSeat = () => request("/teams/lite-seat/add", { method: "POST" });
export const inviteAgent = (data) => request("/teams/invite", { method: "POST", body: JSON.stringify(data) });
export const acceptInvite = (data) => request("/teams/accept-invite", { method: "PUT", body: JSON.stringify(data) });
export const rejectInvite = (data) => request("/teams/reject-invite", { method: "PUT", body: JSON.stringify(data) });
export const resendInvite = (invitationId) => request("/teams/resend-invite", { method: "POST", body: JSON.stringify({ invitation_id: invitationId }) });
export const removeMember = (userId, teamId) => request(`/teams/remove-member/${userId}`, { method: "DELETE", body: JSON.stringify({ team_id: teamId }) });
export const deactivateAgent = (userId, teamId) => request(`/teams/deactivate-agent/${userId}`, { method: "PUT", body: JSON.stringify({ team_id: teamId }) });
export const activateAgent = (userId, teamId) => request(`/teams/activate-agent/${userId}`, { method: "PUT", body: JSON.stringify({ team_id: teamId }) });
export const cancelInvitation = (id) => request(`/teams/invitation/${id}`, { method: "DELETE" });
export const assignClient = (data) => request("/teams/assign-client", { method: "PUT", body: JSON.stringify(data) });
export const getTeamStats = (teamId) => request(`/teams/stats?team_id=${teamId}`);
export const getAgentDashboard = (agentId, teamId) => request(`/teams/agent/${agentId}/dashboard?team_id=${teamId}`);
export const deleteTeam = (teamId, userId) => request(`/teams/${teamId}`, { method: "DELETE", body: JSON.stringify({ user_id: userId }) });
export const upgradePlan = (data) => request("/teams/upgrade", { method: "PUT", body: JSON.stringify(data) });

// V1: Agent → Team Join Requests
export const searchTeams = (q) => request(`/teams/search?q=${encodeURIComponent(q)}`);
export const requestToJoinTeam = (team_id, message) => request("/teams/join-requests", { method: "POST", body: JSON.stringify({ team_id, message }) });
export const getIncomingJoinRequests = () => request("/teams/join-requests/incoming");
export const getMyJoinRequests = () => request("/teams/join-requests/mine");
export const approveJoinRequest = (id) => request(`/teams/join-requests/${id}/approve`, { method: "POST" });
export const denyJoinRequest = (id) => request(`/teams/join-requests/${id}/deny`, { method: "POST" });
export const cancelJoinRequest = (id) => request(`/teams/join-requests/${id}`, { method: "DELETE" });

// Emails / Gmail
export const getGmailAuthUrl = (scopes) => request(`/emails/gmail/auth-url?scopes=${scopes.join(",")}`);
export const getGmailStatus = () => request("/emails/gmail/status");
export const disconnectGmail = () => request("/emails/gmail/disconnect", { method: "DELETE" });
export const updateGmailScopes = (scopes) => request(`/emails/gmail/update-scopes?scopes=${scopes.join(",")}`, { method: "PUT" });
export const getEmailLabels = () => request("/emails/labels");
export const getEmails = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/emails/list${query ? `?${query}` : ""}`);
};
export const getEmail = (id) => request(`/emails/${id}`);
export const getUnreadCount = (query) => request(`/emails/unread-count${query ? `?q=${encodeURIComponent(query)}` : ""}`);
export const sendEmailApi = (data) => request("/emails/send", { method: "POST", body: JSON.stringify(data) });
export const getDailyEmailLimit = () => request("/emails/daily-limit");
export const fixEmailTemplates = () => request("/emails/templates/fix", { method: "POST" });
export const replyToEmail = (id, data) => request(`/emails/${id}/reply`, { method: "POST", body: JSON.stringify(data) });
export const forwardEmail = (id, data) => request(`/emails/${id}/forward`, { method: "POST", body: JSON.stringify(data) });
export const starEmail = (id, starred) => request(`/emails/${id}/star`, { method: "PUT", body: JSON.stringify({ starred }) });
export const markEmailRead = (id, read) => request(`/emails/${id}/read`, { method: "PUT", body: JSON.stringify({ read }) });
export const archiveEmail = (id) => request(`/emails/${id}/archive`, { method: "POST" });
export const trashEmail = (id) => request(`/emails/${id}/trash`, { method: "DELETE" });
export const getGoogleWithGmailUrl = (scopes) => request("/auth/google-with-gmail", { method: "POST", body: JSON.stringify({ scopes }) });

// Sent Emails (local DB)
export const getSentEmails = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/emails/sent${query ? `?${query}` : ""}`);
};
export const getContactsWithActivity = () => request("/emails/contacts-with-activity");

// Email Templates
export const getEmailTemplates = () => request("/emails/templates");
export const createEmailTemplate = (data) => request("/emails/templates", { method: "POST", body: JSON.stringify(data) });
export const updateEmailTemplate = (id, data) => request(`/emails/templates/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteEmailTemplate = (id) => request(`/emails/templates/${id}`, { method: "DELETE" });

// Email Contacts
export const getEmailContacts = () => request("/emails/contacts");
export const addEmailContact = (data) => request("/emails/contacts", { method: "POST", body: JSON.stringify(data) });
export const updateEmailContact = (id, data) => request(`/emails/contacts/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteEmailContact = (id) => request(`/emails/contacts/${id}`, { method: "DELETE" });

// Deal Types
export const getDealTypes = () => request("/deal-types");
export const createDealType = (data) => request("/deal-types", { method: "POST", body: JSON.stringify(data) });
export const deleteDealType = (id) => request(`/deal-types/${id}`, { method: "DELETE" });

// Pipelines
export const getPipelines = () => request("/pipelines");
export const getPipeline = (id) => request(`/pipelines/${id}`);
export const createPipeline = (data) => request("/pipelines", { method: "POST", body: JSON.stringify(data) });
export const updatePipeline = (id, data) => request(`/pipelines/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deletePipeline = (id) => request(`/pipelines/${id}`, { method: "DELETE" });
export const updatePipelineStages = (id, stages) => request(`/pipelines/${id}/stages`, { method: "PUT", body: JSON.stringify({ stages }) });
export const reorderPipelines = (order) => request("/pipelines/reorder/bulk", { method: "PUT", body: JSON.stringify({ order }) });

// AI
export const aiSummarize = (client_id) => request("/ai/summarize", { method: "POST", body: JSON.stringify({ client_id }) });
export const aiScoreClient = (client_id) => request("/ai/score-client", { method: "POST", body: JSON.stringify({ client_id }) });
export const aiDraftReplies = (client_id) => request("/ai/draft-replies", { method: "POST", body: JSON.stringify({ client_id }) });
export const aiFollowUpPlan = (client_id) => request("/ai/follow-up-plan", { method: "POST", body: JSON.stringify({ client_id }) });
export const aiDetectStage = (client_id) => request("/ai/detect-stage", { method: "POST", body: JSON.stringify({ client_id }) });
export const aiAsk = (client_id, question) => request("/ai/ask", { method: "POST", body: JSON.stringify({ client_id, question }) });

// Testimonials
export const getPublicTestimonials = () => request("/testimonials/public");
export const submitTestimonial = (data) => request("/testimonials", { method: "POST", body: JSON.stringify(data) });
export const getMyTestimonial = () => request("/testimonials/mine");

// DocGen — AI Document Generation (Ollama)
export const getDocgenDocuments = () => request("/docgen/documents");
export const getDocgenDocument = (type) => request(`/docgen/documents/${type}`);
export const startDocgenSession = () => request("/docgen/session", { method: "POST" });
export const docgenChat = (sessionId, message) => request("/docgen/chat", { method: "POST", body: JSON.stringify({ sessionId, message }) });
export const docgenSave = (sessionId) => request("/docgen/save", { method: "POST", body: JSON.stringify({ sessionId }) });
export const docgenUpdateContract = (id, data) => request(`/docgen/contract/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const getDocgenContracts = () => request("/docgen/contracts");

// Contract Templates (AI Document Generator)
export const getContractTemplates = (category) => request(`/contract-templates${category ? `?category=${category}` : ""}`);
export const getContractTemplate = (id) => request(`/contract-templates/${id}`);
export const createContractTemplate = (data) => request("/contract-templates", { method: "POST", body: JSON.stringify(data) });
export const updateContractTemplate = (id, data) => request(`/contract-templates/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteContractTemplate = (id) => request(`/contract-templates/${id}`, { method: "DELETE" });
export const aiGenerateTemplate = (data) => request("/contract-templates/ai-generate", { method: "POST", body: JSON.stringify(data) });
export const generateContract = (data) => request("/contract-templates/generate", { method: "POST", body: JSON.stringify(data) });
export const updateGeneratedContract = (contractId, data) => request(`/contract-templates/contracts/${contractId}`, { method: "PUT", body: JSON.stringify(data) });
export const aiUpdateContract = (data) => request("/contract-templates/ai-update", { method: "POST", body: JSON.stringify(data) });
export const sendGeneratedContract = (data) => request("/contract-templates/send", { method: "POST", body: JSON.stringify(data) });
export const sendContractTemplate = (id, data) => request(`/contract-templates/${id}/send`, { method: "POST", body: JSON.stringify(data) });
export const sendEnvelope = (data) => request("/contract-templates/send-envelope", { method: "POST", body: JSON.stringify(data) });
export const getAiUsage = () => request("/contract-templates/ai-usage");

// Contract Folders (V1 Section B — folder-centric transaction model)
export const getContractFolders = (params = {}) => {
  // Backwards compatible: callers passing a string still work.
  if (typeof params === "string") params = params ? { workspace: params } : {};
  const query = new URLSearchParams(params).toString();
  return request(`/contract-folders${query ? `?${query}` : ""}`);
};
export const getContractFolder = (id) => request(`/contract-folders/${id}`);
export const createContractFolder = (data, workspace) => request(`/contract-folders${workspace ? `?workspace=${workspace}` : ""}`, { method: "POST", body: JSON.stringify(data) });
export const updateContractFolder = (id, data) => request(`/contract-folders/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const updateFolderStatus = (id, status) => request(`/contract-folders/${id}/status`, { method: "PUT", body: JSON.stringify({ status }) });
export const deleteContractFolder = (id) => request(`/contract-folders/${id}`, { method: "DELETE" });
export const getFolderStates = () => request("/contract-folders/meta/states");
export const addFolderForm = (folderId, templateId) => request(`/contract-folders/${folderId}/forms`, { method: "POST", body: JSON.stringify({ template_id: templateId }) });
export const updateFolderForm = (folderId, formId, data) => request(`/contract-folders/${folderId}/forms/${formId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteFolderForm = (folderId, formId) => request(`/contract-folders/${folderId}/forms/${formId}`, { method: "DELETE" });
export const reorderFolderForms = (folderId, formIds) => request(`/contract-folders/${folderId}/reorder`, { method: "PUT", body: JSON.stringify({ form_ids: formIds }) });

// V1 Section B — Send whole folder for signing (multi-doc envelope; counts as 1 quota unit)
export const sendFolderEnvelope = (folderId, body) =>
  request(`/contract-folders/${folderId}/send-envelope`, { method: "POST", body: JSON.stringify(body) });

// V3 Features 3.1, 3.8-3.11 — broker dashboard
export const getBrokerCompliance = (teamId) => request(`/teams/${teamId}/broker/compliance`);
export const getBrokerPerformance = (teamId, days = 90) => request(`/teams/${teamId}/broker/performance?days=${days}`);
export const getBrokerAtRisk = (teamId) => request(`/teams/${teamId}/broker/at-risk`);
export const getBrokerColdClients = (teamId, limit = 50) => request(`/teams/${teamId}/broker/cold-clients?limit=${limit}`);
export const getBrokerForecast = (teamId) => request(`/teams/${teamId}/broker/forecast`);
// V3 Feature 3.7 — weekly broker AI briefing (Haiku)
export const getBrokerBriefing = (teamId) => request(`/teams/${teamId}/broker/briefing`);
export const generateBrokerBriefing = (teamId) => request(`/teams/${teamId}/broker/briefing`, { method: "POST" });

// V3 Features 3.12 / 3.13 / 3.14 — commission tracking + CDA + disbursements
export const getFolderCommission = (folderId) => request(`/commissions/folder/${folderId}`);
export const upsertFolderCommission = (folderId, data) =>
  request(`/commissions/folder/${folderId}`, { method: "PUT", body: JSON.stringify(data) });
export const addCommissionLineItem = (folderId, data) =>
  request(`/commissions/folder/${folderId}/items`, { method: "POST", body: JSON.stringify(data) });
export const updateCommissionLineItem = (itemId, data) =>
  request(`/commissions/items/${itemId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteCommissionLineItem = (itemId) =>
  request(`/commissions/items/${itemId}`, { method: "DELETE" });
export const generateCda = (folderId) =>
  request(`/commissions/folder/${folderId}/cda`, { method: "POST" });

// V4 Features 4.1 / 4.2 / 4.4 — MLS lookup, search, auto-fill, market context
export const lookupMlsListing = (mlsNumber) => request(`/mls/listing/${encodeURIComponent(mlsNumber)}`);
export const searchMlsListings = (q) => request(`/mls/search?q=${encodeURIComponent(q)}`);
export const getMarketContext = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/mls/market${query ? `?${query}` : ""}`);
};
export const autoFillFolderFromMls = (folderId, mlsNumber, overwrite = false) =>
  request(`/mls/folders/${folderId}/autofill`, {
    method: "POST",
    body: JSON.stringify({ mls_number: mlsNumber, overwrite }),
  });

// V4 Features 4.6 / 4.7 — Lead capture widget + IDX webhooks
export const getWidgetToken = () => request("/lead-capture/token");
export const rotateWidgetToken = () => request("/lead-capture/token/rotate", { method: "POST" });
export const getIdxSecrets = () => request("/lead-capture/idx");
export const createIdxSecret = (provider) =>
  request(`/lead-capture/idx/${provider}`, { method: "POST" });

// V4 Feature 4.8 — Google Calendar sync
export const getCalendarStatus = () => request("/calendar/status");
export const enableCalendarSync = () => request("/calendar/enable", { method: "POST" });
export const disableCalendarSync = () => request("/calendar/disable", { method: "POST" });
export const resyncCalendar = () => request("/calendar/resync", { method: "POST" });

// V4 Features 4.9 / 4.10 — Webhooks + API keys
export const getEventCatalog = () => request("/integrations/events");
export const listWebhooks = () => request("/integrations/webhooks");
export const createWebhook = (data) => request("/integrations/webhooks", { method: "POST", body: JSON.stringify(data) });
export const deleteWebhook = (id) => request(`/integrations/webhooks/${id}`, { method: "DELETE" });
export const listApiKeys = () => request("/integrations/api-keys");
export const createApiKey = (label) => request("/integrations/api-keys", { method: "POST", body: JSON.stringify({ label }) });
export const revokeApiKey = (id) => request(`/integrations/api-keys/${id}`, { method: "DELETE" });

// V4 Feature 4.11 — SMS campaigns (Twilio)
export const previewSmsSegment = (filter) =>
  request("/sms/preview", { method: "POST", body: JSON.stringify({ filter }) });
export const listSmsCampaigns = () => request("/sms/campaigns");
export const getSmsCampaign = (id) => request(`/sms/campaigns/${id}`);
export const createSmsCampaign = (data) =>
  request("/sms/campaigns", { method: "POST", body: JSON.stringify(data) });
export const sendSmsCampaign = (id) =>
  request(`/sms/campaigns/${id}/send`, { method: "POST" });

// ───────────────────────────────────────────────────────────
// V5 Features
// ───────────────────────────────────────────────────────────

// V5 5.1 / 5.2 / 5.3 — White-label branding
export const getMyBranding = () => request("/branding/me");
export const getTeamBranding = (teamId) => request(`/branding/teams/${teamId}`);
export const updateTeamBranding = (teamId, patch) =>
  request(`/branding/teams/${teamId}`, { method: "PUT", body: JSON.stringify(patch) });
export const verifyDomain = (teamId) =>
  request(`/branding/teams/${teamId}/verify-domain`, { method: "POST" });

// V5 5.5 / 5.6 — Organizations (multi-office hierarchy)
export const listMyOrgs = () => request("/organizations");
export const createOrganization = (data) =>
  request("/organizations", { method: "POST", body: JSON.stringify(data) });
export const getOrganization = (id) => request(`/organizations/${id}`);
export const getOrgTeams = (id) => request(`/organizations/${id}/teams`);
export const getOrgFolders = (id, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/organizations/${id}/folders${query ? `?${query}` : ""}`);
};
export const getOrgPerformance = (id) => request(`/organizations/${id}/performance`);
export const attachTeamToOrg = (id, teamId) =>
  request(`/organizations/${id}/attach-team`, { method: "POST", body: JSON.stringify({ team_id: teamId }) });

// V5 5.8 — Advanced ACL
export const getRoleCatalog = () => request("/acl/roles");
export const getPermissionCatalog = () => request("/acl/permissions");
export const getMyPermissions = () => request("/acl/me");
export const getUserRoles = (userId) => request(`/acl/users/${userId}`);
export const assignRole = (userId, role, scopeType, scopeId) =>
  request(`/acl/users/${userId}`, { method: "POST", body: JSON.stringify({ role, scopeType, scopeId }) });
export const revokeRoleAssignment = (assignmentId) =>
  request(`/acl/assignments/${assignmentId}`, { method: "DELETE" });

// V5 5.9 — SSO
export const discoverSso = (email) => request(`/sso/discover?email=${encodeURIComponent(email)}`);
export const getSsoConfig = (orgId) => request(`/sso/config/org/${orgId}`);
export const updateSsoConfig = (orgId, patch) =>
  request(`/sso/config/org/${orgId}`, { method: "PUT", body: JSON.stringify(patch) });

// V5 5.10 — Back-office accounting
export const listCommissionPlans = () => request("/back-office/plans");
export const createCommissionPlan = (data) =>
  request("/back-office/plans", { method: "POST", body: JSON.stringify(data) });
export const updateCommissionPlan = (id, data) =>
  request(`/back-office/plans/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const assignCommissionPlan = (planId, userId, effectiveFrom) =>
  request(`/back-office/plans/${planId}/assign`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, effective_from: effectiveFrom }),
  });
export const previewSplit = (userId, dealCommissionCents) =>
  request(`/back-office/users/${userId}/split-preview?deal_commission_cents=${dealCommissionCents}`);
// QuickBooks export is a CSV download — caller uses the URL directly:
export const quickbooksExportUrl = (startDate, endDate) => {
  const base = process.env.REACT_APP_API_URL || "http://localhost:5001";
  const q = new URLSearchParams();
  if (startDate) q.set("start_date", startDate);
  if (endDate) q.set("end_date", endDate);
  return `${base}/back-office/quickbooks-export.csv?${q.toString()}`;
};

// V5 5.11 — Data rights (CCPA/CPRA)
export const listDataRequests = () => request("/data-rights/requests");
export const requestAccountDeletion = (reason) =>
  request("/data-rights/deletion", { method: "POST", body: JSON.stringify({ reason }) });
export const cancelDeletionRequest = (id) =>
  request(`/data-rights/deletion/${id}`, { method: "DELETE" });
export const dataExportUrl = () => {
  const base = process.env.REACT_APP_API_URL || "http://localhost:5001";
  return `${base}/data-rights/export.json`;
};

// V3 Features 3.2 / 3.3 / 3.5 — broker approval workflow + audit queue
export const getAuditQueue = (teamId) => request(`/contract-folders/meta/audit-queue?teamId=${teamId}`);
export const submitFolderForReview = (folderId) =>
  request(`/contract-folders/${folderId}/submit-for-review`, { method: "POST" });
export const brokerApproveFolder = (folderId, body) =>
  request(`/contract-folders/${folderId}/broker-approve`, { method: "POST", body: JSON.stringify(body || {}) });
export const brokerRequestChanges = (folderId, comments) =>
  request(`/contract-folders/${folderId}/broker-request-changes`, { method: "POST", body: JSON.stringify({ comments }) });

// V2 Features 2.3 / 2.8 / 2.10 — folder/deal health + stalled detection
export const getFolderHealth = (folderId) => request(`/contract-folders/${folderId}/health`);
export const getDealHealth = (dealId) => request(`/deals/${dealId}/health`);
export const getStalledFolders = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/contract-folders/meta/stalled${query ? `?${query}` : ""}`);
};

// V2 Feature 2.1 — AI suggested missing documents (with explanations + extras)
export const aiSuggestMissingDocs = (folderId) =>
  request(`/contract-folders/${folderId}/ai/suggest-missing`, { method: "POST" });

// V2 Feature 2.4 — AI envelope cover sheet (friendly intro for signers)
export const aiCoverSheet = (folderId, signerName) =>
  request(`/contract-folders/${folderId}/ai/cover-sheet`, { method: "POST", body: JSON.stringify({ signerName }) });

// V2 Feature 2.7 — AI client email drafting
export const aiDraftClientEmail = (body) =>
  request("/ai/draft-client-email", { method: "POST", body: JSON.stringify(body) });

// V2 Feature 2.2 — Compliance checklist per folder
export const getFolderCompliance = (folderId) => request(`/contract-folders/${folderId}/compliance`);
export const getFolderComplianceMissing = (folderId) => request(`/contract-folders/${folderId}/compliance/missing`);
export const updateComplianceItem = (folderId, itemId, body) =>
  request(`/contract-folders/${folderId}/compliance/${itemId}`, { method: "PUT", body: JSON.stringify(body) });
export const addComplianceRequirement = (folderId, body) =>
  request(`/contract-folders/${folderId}/compliance`, { method: "POST", body: JSON.stringify(body) });

// V1 Section B — Per-folder activity feed (audit trail)
export const getFolderActivity = (folderId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/contract-folders/${folderId}/activity${query ? `?${query}` : ""}`);
};

// V1 Section B — Folder permissions (owner / editor / viewer)
export const getFolderPermissions = (folderId) => request(`/contract-folders/${folderId}/permissions`);
export const grantFolderPermission = (folderId, email, role) =>
  request(`/contract-folders/${folderId}/permissions`, { method: "POST", body: JSON.stringify({ email, role }) });
export const revokeFolderPermission = (folderId, userId) =>
  request(`/contract-folders/${folderId}/permissions/${userId}`, { method: "DELETE" });

// V3 Feature 3.6 — Per-resource ACL overrides (hide specific docs from specific users)
export const getResourcePermissions = (folderId) =>
  request(`/contract-folders/${folderId}/resource-permissions`);
export const setResourcePermission = (folderId, body) =>
  request(`/contract-folders/${folderId}/resource-permissions`, { method: "POST", body: JSON.stringify(body) });
export const removeResourcePermission = (folderId, permId) =>
  request(`/contract-folders/${folderId}/resource-permissions/${permId}`, { method: "DELETE" });

// V1 Section B — Folder templates (Buyer/Seller bundles)
export const getFolderTemplates = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/contract-folders/templates${query ? `?${query}` : ""}`);
};
export const applyFolderTemplate = (folderId, folderTemplateId) =>
  request(`/contract-folders/${folderId}/apply-template`, {
    method: "POST",
    body: JSON.stringify({ folder_template_id: folderTemplateId }),
  });
export const saveFolderTemplate = (data) =>
  request("/contract-folders/templates", { method: "POST", body: JSON.stringify(data) });
// V3 Feature 3.4 — broker library: edit/delete folder templates (team-scoped supported)
export const updateFolderTemplate = (id, data) =>
  request(`/contract-folders/templates/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteFolderTemplate = (id) =>
  request(`/contract-folders/templates/${id}`, { method: "DELETE" });

// V1 Section B — Folder uploads (PDFs, scans, etc.)
export const getFolderUploads = (folderId) => request(`/contract-folders/${folderId}/uploads`);
export const requestFolderUploadUrl = (folderId, body) =>
  request(`/contract-folders/${folderId}/uploads/request-upload`, { method: "POST", body: JSON.stringify(body) });
export const confirmFolderUpload = (folderId, body) =>
  request(`/contract-folders/${folderId}/uploads/confirm`, { method: "POST", body: JSON.stringify(body) });
export const updateFolderUpload = (folderId, uploadId, body) =>
  request(`/contract-folders/${folderId}/uploads/${uploadId}`, { method: "PUT", body: JSON.stringify(body) });
export const getFolderUploadDownloadUrl = (folderId, uploadId) =>
  request(`/contract-folders/${folderId}/uploads/${uploadId}/download`);
export const deleteFolderUpload = (folderId, uploadId) =>
  request(`/contract-folders/${folderId}/uploads/${uploadId}`, { method: "DELETE" });

// Helper: end-to-end upload flow (request URL → PUT to GCS → confirm).
// Returns the confirmed upload row.
export const uploadFileToFolder = async (folderId, file, label) => {
  const { signedUrl, gcsPath } = await requestFolderUploadUrl(folderId, {
    fileName: file.name, mimeType: file.type, fileSize: file.size,
  });
  const putResp = await fetch(signedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putResp.ok) throw new Error("File upload to storage failed");
  return confirmFolderUpload(folderId, {
    gcsPath, fileName: file.name, mimeType: file.type, fileSize: file.size, label,
  });
};
export const downloadTemplatePdf = async (id) => {
  const token = getToken();
  const res = await fetch(`${API_BASE}/contract-templates/${id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to download PDF");
  return res.blob();
};

// Billing
export const getBillingPlans = () => request("/billing/plans");
export const getBillingStatus = () => request("/billing/status");
export const createCheckout = (plan, interval = "monthly") => request("/billing/create-checkout", { method: "POST", body: JSON.stringify({ plan, interval }) });
export const createBillingPortal = () => request("/billing/create-portal", { method: "POST", body: JSON.stringify({}) });
export const useFreeSigning = () => request("/billing/use-free-signing", { method: "POST", body: JSON.stringify({}) });
export const getPaymentMethod = () => request("/billing/payment-method");
export const getInvoices = () => request("/billing/invoices");
export const getSubscription = () => request("/billing/subscription");
export const changePlan = (plan, interval = "monthly") => request("/billing/change-plan", { method: "POST", body: JSON.stringify({ plan, interval }) });
export const cancelSubscription = (reason) => request("/billing/cancel", { method: "POST", body: JSON.stringify({ reason }) });
export const resumeSubscription = () => request("/billing/resume", { method: "POST", body: JSON.stringify({}) });
export const getSigningBalance = () => request("/billing/signing-balance");
export const getSigningAddons = () => request("/billing/signing-addons");
export const purchaseSignings = (productType) => request("/billing/purchase-signings", { method: "POST", body: JSON.stringify({ productType }) });
export const getEsignQuota = () => request("/billing/quota");
export const confirmSigningPurchase = (data) => request("/billing/confirm-signing-purchase", { method: "POST", body: JSON.stringify(data) });
export const getTeamUsage = (teamId) => request(`/billing/team-usage?team_id=${teamId}`);
export const getTeamBreakdown = () => request("/billing/team-breakdown");
export const getSigningHistory = (teamId) => request(`/billing/signing-history${teamId ? `?team_id=${teamId}` : ""}`);

// Team Seats
export const getTeamSeats = (teamId) => request(`/teams/${teamId}/seats`);
export const addTeamSeats = (teamId, quantity) => request(`/teams/${teamId}/seats/add`, { method: "POST", body: JSON.stringify({ quantity }) });

// Client Documents
export const requestDocumentUpload = (clientId, data) => request(`/clients/${clientId}/documents/request-upload`, { method: "POST", body: JSON.stringify(data) });
export const confirmDocumentUpload = (clientId, data) => request(`/clients/${clientId}/documents/confirm`, { method: "POST", body: JSON.stringify(data) });
export const getClientDocuments = (clientId) => request(`/clients/${clientId}/documents`);
export const deleteClientDocument = (clientId, documentId) => request(`/clients/${clientId}/documents/${documentId}`, { method: "DELETE" });
export const getStorageUsage = () => request("/storage/usage");

// Form Library
export const getFormLibrary = () => request("/forms/library");
export const getFormDetail = (id) => request(`/forms/library/${id}`);

// Transaction Packets
export const createPacket = (data) => request("/packets", { method: "POST", body: JSON.stringify(data) });
export const getPackets = (params) => request(`/packets${params ? "?" + new URLSearchParams(params) : ""}`);
export const getPacket = (id) => request(`/packets/${id}`);
export const updatePacket = (id, data) => request(`/packets/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deletePacket = (id) => request(`/packets/${id}`, { method: "DELETE" });
export const addPacketForms = (packetId, formIds) => request(`/packets/${packetId}/forms`, { method: "POST", body: JSON.stringify({ form_ids: formIds }) });
export const removePacketForm = (packetId, formId) => request(`/packets/${packetId}/forms/${formId}`, { method: "DELETE" });
export const reorderPacketForms = (packetId, order) => request(`/packets/${packetId}/forms/reorder`, { method: "PUT", body: JSON.stringify({ order }) });
export const getPacketForm = (packetId, formId) => request(`/packets/${packetId}/forms/${formId}`);
export const savePacketFormFields = (packetId, formId, data) => request(`/packets/${packetId}/forms/${formId}/fields`, { method: "PUT", body: JSON.stringify(data) });
export const markPacketFormComplete = (packetId, formId, isComplete) => request(`/packets/${packetId}/forms/${formId}/complete`, { method: "PUT", body: JSON.stringify({ is_complete: isComplete }) });
export const getPacketSharedFields = (packetId) => request(`/packets/${packetId}/shared-fields`);
export const updatePacketSharedFields = (packetId, fields) => request(`/packets/${packetId}/shared-fields`, { method: "PUT", body: JSON.stringify({ fields }) });
export const previewPacketForm = (packetId, formId) => request(`/packets/${packetId}/forms/${formId}/preview`);
export const sendPacket = (packetId, signers) => request(`/packets/${packetId}/send`, { method: "POST", body: JSON.stringify({ signers }) });
export const voidPacket = (packetId) => request(`/packets/${packetId}/void`, { method: "POST" });
export const getPacketStatus = (packetId) => request(`/packets/${packetId}/status`);
